import { removeProfilePicture, updateProfilePicture } from "api/auth";
import { UserAvatar } from "components/UserAvatar";
import LineIcon from "pages/HomePage/components/LineIcon";
import React, { type ChangeEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { compressImage } from "utilities";

import type { AvatarUploaderProps } from "../settings.types";

import styles from "./AvatarUploader.module.css";

/**
 * The profile photo control on the General pane. Clicking the avatar opens the file picker; the
 * selected image is compressed in the browser (to ~100 KB) before upload, keeping stored blobs small.
 */
const AvatarUploader = ({ initials }: AvatarUploaderProps) => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken, setUser, user } = useSession();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isBusy, setIsBusy] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const imageUrl = user?.profileImageUrl ?? null;

	const openFilePicker = useCallback((): void => fileInputRef.current?.click(), []);

	const onFileChange = useCallback(
		async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
			const file = event.target.files?.[0];
			// Reset so picking the same file again still fires a change event.
			event.target.value = "";
			if (!file || isBusy) {
				return;
			}

			setErrorMessage("");
			setIsBusy(true);
			try {
				const token = await getAccessToken();
				if (!token) {
					return;
				}

				const compressed = await compressImage(file);
				const account = await updateProfilePicture(compressed, token);
				setUser(account);
			} catch (error) {
				setErrorMessage(error instanceof Error ? error.message : t("general.photoError"));
			} finally {
				setIsBusy(false);
			}
		},
		[getAccessToken, isBusy, setUser, t]
	);

	const onRemove = useCallback(async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setErrorMessage("");
		setIsBusy(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const account = await removeProfilePicture(token);
			setUser(account);
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("general.photoError"));
		} finally {
			setIsBusy(false);
		}
	}, [getAccessToken, isBusy, setUser, t]);

	return (
		<div className={styles.root}>
			<button
				aria-label={t("general.changePhoto")}
				className={styles.avatarButton}
				disabled={isBusy}
				onClick={openFilePicker}
				title={t("general.changePhoto")}
				type="button">
				<UserAvatar className={styles.avatar} imageUrl={imageUrl} initials={initials} />
				<span aria-hidden="true" className={styles.overlay}>
					<LineIcon name="camera" />
				</span>
			</button>

			<input
				ref={fileInputRef}
				accept="image/jpeg,image/png,image/webp"
				className={styles.fileInput}
				onChange={onFileChange}
				type="file"
			/>

			{isBusy && <span className={styles.status}>{t("general.photoUploading")}</span>}
			{!isBusy && imageUrl && (
				<button className={styles.removeButton} onClick={onRemove} type="button">
					{t("general.removePhoto")}
				</button>
			)}
			{errorMessage && <span className={styles.error}>{errorMessage}</span>}
		</div>
	);
};

export default AvatarUploader;
